import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Snowflake, Thermometer, Plus, AlertTriangle, Clock, Settings, Search, RefreshCw, Package, Filter } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { FreezerOutTimeTimer } from "@/components/Freezer";

interface Freezer {
  id: string;
  code: string;
  name: string;
  location: string | null;
  min_temp_threshold: number;
  max_temp_threshold: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
}

interface TemperatureLog {
  id: string;
  freezer_id: string;
  temperature: number;
  shift_type: "start" | "end";
  recorded_by: string | null;
  recorded_at: string;
  notes: string | null;
  is_out_of_range: boolean;
  freezer?: Freezer;
  profile?: { full_name: string };
}

interface FreezerItem {
  id: string;
  source: "plotter_cut" | "material_lot";
  name: string;
  code?: string;
  batchNumber?: string;
  removedAt: string;
  maxMinutes: number;
  percentRemaining: number;
}

export default function FreezerControl() {
  const { user, userRole } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [mainTab, setMainTab] = useState<"outside" | "temperature">("outside");

  // Temperature state
  const [selectedFreezer, setSelectedFreezer] = useState<string>("");
  const [temperature, setTemperature] = useState<string>("");
  const [shiftType, setShiftType] = useState<"start" | "end">("start");
  const [customTime, setCustomTime] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [addFreezerOpen, setAddFreezerOpen] = useState(false);
  const [newFreezer, setNewFreezer] = useState({
    code: "",
    name: "",
    location: "",
    min_temp_threshold: "-25",
    max_temp_threshold: "-18",
  });

  // Outside materials state
  const [searchTerm, setSearchTerm] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState<"all" | "critical" | "warning">("all");

  const isAdmin = userRole === "admin" || userRole === "superadmin";

  // ========== OUTSIDE MATERIALS QUERY ==========
  const { data: freezerItems = [], isLoading: loadingItems, refetch: refetchItems } = useQuery({
    queryKey: ["freezer-outside-items"],
    queryFn: async () => {
      const now = Date.now();
      const items: FreezerItem[] = [];

      // Plotter cuts fuera del congelador
      const { data: plotterData } = await api
        .from("plotter_cuts")
        .select("id, package_code, package_name, batch_number, removed_from_freezer_at, max_out_time_minutes")
        .gt("removed_from_freezer_at", "1970-01-01")
        .in("status", ["available", "reserved", "in_use"]);

      plotterData?.forEach((pkg: any) => {
        if (!pkg.removed_from_freezer_at) return;
        const removedAt = new Date(pkg.removed_from_freezer_at).getTime();
        const minutesElapsed = Math.floor((now - removedAt) / 60000);
        const maxMin = pkg.max_out_time_minutes || 120;
        const minutesRemaining = Math.max(0, maxMin - minutesElapsed);
        items.push({
          id: pkg.id,
          source: "plotter_cut",
          name: pkg.package_name || "Paquete Plotter",
          code: pkg.package_code,
          batchNumber: pkg.batch_number,
          removedAt: pkg.removed_from_freezer_at,
          maxMinutes: maxMin,
          percentRemaining: (minutesRemaining / maxMin) * 100,
        });
      });

      // Material lots fuera del congelador
      const { data: lotData } = await api
        .from("material_lots")
        .select("id, batch_number, roll_number, removed_from_freezer_at, is_currently_out, material_id")
        .eq("is_currently_out", true)
        .gt("removed_from_freezer_at", "1970-01-01");

      // Obtener info de materiales (evitamos embeds)
      const materialIds = [...new Set(lotData?.map((l: any) => l.material_id).filter(Boolean) || [])];
      const materialsMap: Record<string, { name: string; max_out_time_minutes: number; is_freezer_material: boolean }> = {};

      if (materialIds.length > 0) {
        const { data: materials } = await api
          .from("material_inventory")
          .select("id, name, max_out_time_minutes, is_freezer_material")
          .in("id", materialIds);

        materials?.forEach((m: any) => {
          materialsMap[m.id] = m;
        });
      }

      lotData?.forEach((lot: any) => {
        if (!lot.removed_from_freezer_at) return;
        const material = materialsMap[lot.material_id];
        if (!material?.is_freezer_material) return;

        const removedAt = new Date(lot.removed_from_freezer_at).getTime();
        const minutesElapsed = Math.floor((now - removedAt) / 60000);
        const maxMin = material.max_out_time_minutes || 120;
        const minutesRemaining = Math.max(0, maxMin - minutesElapsed);
        items.push({
          id: lot.id,
          source: "material_lot",
          name: material.name || "Lote Material",
          batchNumber: lot.batch_number || lot.roll_number,
          removedAt: lot.removed_from_freezer_at,
          maxMinutes: maxMin,
          percentRemaining: (minutesRemaining / maxMin) * 100,
        });
      });

      return items.sort((a, b) => a.percentRemaining - b.percentRemaining);
    },
    refetchInterval: 30000,
  });

  const handleReturnToFreezer = async (item: FreezerItem) => {
    try {
      if (item.source === "plotter_cut") {
        const { error } = await api
          .from("plotter_cuts")
          .update({ removed_from_freezer_at: null })
          .eq("id", item.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from("material_lots")
          .update({
            returned_to_freezer_at: new Date().toISOString(),
            is_currently_out: false,
          })
          .eq("id", item.id);
        if (error) throw error;
      }
      toast.success("Material guardado en congelador");
      refetchItems();
    } catch (err: any) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const filteredItems = freezerItems.filter((item) => {
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase()) && !item.code?.toLowerCase().includes(searchTerm.toLowerCase()) && !item.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (urgencyFilter === "critical") return item.percentRemaining <= 10;
    if (urgencyFilter === "warning") return item.percentRemaining <= 25;
    return true;
  });

  const stats = {
    total: freezerItems.length,
    critical: freezerItems.filter((i) => i.percentRemaining <= 10).length,
    warning: freezerItems.filter((i) => i.percentRemaining > 10 && i.percentRemaining <= 25).length,
    ok: freezerItems.filter((i) => i.percentRemaining > 25).length,
  };

  // ========== TEMPERATURE QUERIES ==========
  const { data: freezers = [], isLoading: loadingFreezers } = useQuery({
    queryKey: ["freezers"],
    queryFn: async () => {
      const { data, error } = await api
        .from("freezers")
        .select("*")
        .eq("is_active", true)
        .order("code");
      if (error) throw error;
      return data as Freezer[];
    },
  });

  const { data: temperatureLogs = [], isLoading: loadingLogs } = useQuery({
    queryKey: ["freezer-temperature-logs"],
    queryFn: async () => {
      const { data, error } = await api
        .from("freezer_temperature_logs")
        .select("*")
        .order("recorded_at", { ascending: false })
        .limit(100);
      if (error) throw error;

      // Obtener freezers y profiles por separado (evitamos embeds)
      const freezerIds = [...new Set(data?.map((l: any) => l.freezer_id).filter(Boolean) || [])];
      const profileIds = [...new Set(data?.map((l: any) => l.recorded_by).filter(Boolean) || [])];

      const freezersMap: Record<string, Freezer> = {};
      const profilesMap: Record<string, { full_name: string }> = {};

      if (freezerIds.length > 0) {
        const { data: frzs } = await api.from("freezers").select("*").in("id", freezerIds);
        frzs?.forEach((f: any) => { freezersMap[f.id] = f; });
      }

      if (profileIds.length > 0) {
        const { data: prfs } = await api.from("profiles").select("id, full_name").in("id", profileIds);
        prfs?.forEach((p: any) => { profilesMap[p.id] = p; });
      }

      return data?.map((log: any) => ({
        ...log,
        freezer: freezersMap[log.freezer_id],
        profile: profilesMap[log.recorded_by],
      })) as TemperatureLog[];
    },
  });

  const logTemperatureMutation = useMutation({
    mutationFn: async (payload: {
      freezer_id: string;
      temperature: number;
      shift_type: "start" | "end";
      notes?: string;
      recorded_at?: string;
    }) => {
      const { data, error } = await api
        .from("freezer_temperature_logs")
        .insert({
          ...payload,
          recorded_by: user?.id,
          ...(payload.recorded_at ? { recorded_at: payload.recorded_at } : {}),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["freezer-temperature-logs"] });
      setTemperature("");
      setNotes("");
      setCustomTime("");
      const freezer = freezers.find((f) => f.id === data.freezer_id);
      if (data.is_out_of_range) {
        toast.warning(
          `Temperatura ${data.temperature}°C registrada en ${freezer?.name} - FUERA DE RANGO`,
          { duration: 8000 }
        );
      } else {
        toast.success(`Temperatura ${data.temperature}°C registrada en ${freezer?.name}`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Error al registrar temperatura: ${error.message}`);
    },
  });

  const addFreezerMutation = useMutation({
    mutationFn: async (payload: typeof newFreezer) => {
      const { data, error } = await api
        .from("freezers")
        .insert({
          code: payload.code,
          name: payload.name,
          location: payload.location || null,
          min_temp_threshold: parseFloat(payload.min_temp_threshold),
          max_temp_threshold: parseFloat(payload.max_temp_threshold),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["freezers"] });
      setAddFreezerOpen(false);
      setNewFreezer({
        code: "",
        name: "",
        location: "",
        min_temp_threshold: "-25",
        max_temp_threshold: "-18",
      });
      toast.success(`Congelador ${data.name} añadido`);
    },
    onError: (error: Error) => {
      toast.error(`Error al añadir congelador: ${error.message}`);
    },
  });

  const handleLogTemperature = () => {
    if (!selectedFreezer || !temperature) {
      toast.error("Selecciona un congelador e introduce la temperatura");
      return;
    }
    const temp = parseFloat(temperature);
    if (isNaN(temp) || temp < -50 || temp > 10) {
      toast.error("Temperatura inválida (rango válido: -50°C a 10°C)");
      return;
    }
    const recordedAt = customTime
      ? new Date(`${new Date().toISOString().split('T')[0]}T${customTime}:00`).toISOString()
      : undefined;
    logTemperatureMutation.mutate({
      freezer_id: selectedFreezer,
      temperature: temp,
      shift_type: shiftType,
      notes: notes || undefined,
      recorded_at: recordedAt,
    });
  };

  const handleAddFreezer = () => {
    if (!newFreezer.code || !newFreezer.name) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    addFreezerMutation.mutate(newFreezer);
  };

  const todayLogs = temperatureLogs.filter((log) => {
    const logDate = new Date(log.recorded_at).toDateString();
    return logDate === new Date().toDateString();
  });

  const outOfRangeLogs = temperatureLogs.filter((log) => log.is_out_of_range);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Snowflake className="h-8 w-8 text-blue-500" />
            {t.freezer.title}
          </h1>
          <div className="flex items-center gap-2">
            {mainTab === "outside" && (
              <Button variant="outline" onClick={() => refetchItems()}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            )}
            {isAdmin && mainTab === "temperature" && (
              <Dialog open={addFreezerOpen} onOpenChange={setAddFreezerOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Añadir Congelador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuevo Congelador</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Código *</Label>
                        <Input
                          placeholder="CONG-03"
                          value={newFreezer.code}
                          onChange={(e) =>
                            setNewFreezer({ ...newFreezer, code: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label>Nombre *</Label>
                        <Input
                          placeholder="Congelador Auxiliar"
                          value={newFreezer.name}
                          onChange={(e) =>
                            setNewFreezer({ ...newFreezer, name: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Ubicación</Label>
                      <Input
                        placeholder="Área de producción"
                        value={newFreezer.location}
                        onChange={(e) =>
                          setNewFreezer({ ...newFreezer, location: e.target.value })
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Temp. Mínima (°C)</Label>
                        <Input
                          type="number"
                          value={newFreezer.min_temp_threshold}
                          onChange={(e) =>
                            setNewFreezer({
                              ...newFreezer,
                              min_temp_threshold: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Temp. Máxima (°C)</Label>
                        <Input
                          type="number"
                          value={newFreezer.max_temp_threshold}
                          onChange={(e) =>
                            setNewFreezer({
                              ...newFreezer,
                              max_temp_threshold: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleAddFreezer}
                      disabled={addFreezerMutation.isPending}
                    >
                      {addFreezerMutation.isPending ? "Añadiendo..." : "Añadir Congelador"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Main tabs */}
        <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as "outside" | "temperature")}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="outside" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t.freezer.materialsOutside}
              {stats.total > 0 && (
                <Badge variant={stats.critical > 0 ? "destructive" : "secondary"} className="ml-1">
                  {stats.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="temperature" className="flex items-center gap-2">
              <Thermometer className="h-4 w-4" />
              {t.freezer.temperatures}
            </TabsTrigger>
          </TabsList>

          {/* ========== TAB: MATERIALES FUERA ========== */}
          <TabsContent value="outside" className="space-y-6 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t.freezer.totalOutside}</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <Snowflake className="w-8 h-8 text-blue-500/30" />
                </div>
              </Card>
              <Card className={`p-4 ${stats.critical > 0 ? "border-red-200 bg-red-50/50 dark:bg-red-950/30" : ""}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">{t.freezer.critical}</p>
                    <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
                  </div>
                  <AlertTriangle className={`w-8 h-8 ${stats.critical > 0 ? "text-red-500 animate-pulse" : "text-red-500/30"}`} />
                </div>
              </Card>
              <Card className={`p-4 ${stats.warning > 0 ? "border-yellow-200 bg-yellow-50/50 dark:bg-yellow-950/30" : ""}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-600">{t.freezer.warning}</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.warning}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500/30" />
                </div>
              </Card>
              <Card className="p-4 border-green-200 bg-green-50/50 dark:bg-green-950/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">{t.freezer.ok}</p>
                    <p className="text-2xl font-bold text-green-600">{stats.ok}</p>
                  </div>
                  <Package className="w-8 h-8 text-green-500/30" />
                </div>
              </Card>
            </div>

            {/* Search and filters */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, código o lote..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={urgencyFilter} onValueChange={(v) => setUrgencyFilter(v as any)}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="critical">Solo Críticos</SelectItem>
                  <SelectItem value="warning">Advertencia+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Grid of timer cards */}
            {loadingItems ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              </div>
            ) : filteredItems.length === 0 ? (
              <Card className="p-12 text-center">
                <Snowflake className="w-16 h-16 mx-auto text-blue-500/30 mb-4" />
                <p className="text-lg text-muted-foreground">
                  {freezerItems.length === 0
                    ? "No hay materiales fuera del congelador"
                    : "No hay materiales que coincidan con el filtro"}
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredItems.map((item) => (
                  <FreezerOutTimeTimer
                    key={item.source + "-" + item.id}
                    removedAt={item.removedAt}
                    maxMinutes={item.maxMinutes}
                    materialName={item.name}
                    batchNumber={item.batchNumber || item.code}
                    onReturn={async () => {
                      await handleReturnToFreezer(item);
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== TAB: TEMPERATURAS ========== */}
          <TabsContent value="temperature" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Panel de registro */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Thermometer className="h-5 w-5" />
                    Registrar Temperatura
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Congelador</Label>
                    <Select value={selectedFreezer} onValueChange={setSelectedFreezer}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar congelador..." />
                      </SelectTrigger>
                      <SelectContent>
                        {freezers.map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.code} - {f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Temperatura (°C)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="-20.5"
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleLogTemperature();
                        }
                      }}
                    />
                  </div>

                  <div>
                    <Label>Momento del Turno</Label>
                    <Select value={shiftType} onValueChange={(v: "start" | "end") => setShiftType(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="start">
                          <span className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-green-500" />
                            Inicio de turno
                          </span>
                        </SelectItem>
                        <SelectItem value="end">
                          <span className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-orange-500" />
                            Fin de turno
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Hora del registro (opcional)</Label>
                    <Input
                      type="time"
                      value={customTime}
                      onChange={(e) => setCustomTime(e.target.value)}
                      placeholder="Dejar vacío para hora actual"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Dejar vacío para usar la hora actual
                    </p>
                  </div>

                  <div>
                    <Label>Notas (opcional)</Label>
                    <Textarea
                      placeholder="Observaciones..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                    />
                  </div>

                  <Button
                    className="w-full"
                    onClick={handleLogTemperature}
                    disabled={logTemperatureMutation.isPending || !selectedFreezer || !temperature}
                  >
                    {logTemperatureMutation.isPending ? "Registrando..." : "Registrar Temperatura"}
                  </Button>
                </CardContent>
              </Card>

              {/* Panel de congeladores y resumen */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Estado de Congeladores
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {freezers.map((freezer) => {
                      const freezerTodayLogs = todayLogs.filter(
                        (log) => log.freezer_id === freezer.id
                      );
                      const lastLog = freezerTodayLogs[0];
                      const hasStartLog = freezerTodayLogs.some((l) => l.shift_type === "start");
                      const hasEndLog = freezerTodayLogs.some((l) => l.shift_type === "end");

                      return (
                        <Card
                          key={freezer.id}
                          className={`p-4 ${
                            lastLog?.is_out_of_range
                              ? "border-red-500 bg-red-50 dark:bg-red-950"
                              : ""
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold">{freezer.name}</h3>
                              <p className="text-sm text-muted-foreground">{freezer.code}</p>
                              {freezer.location && (
                                <p className="text-xs text-muted-foreground">{freezer.location}</p>
                              )}
                            </div>
                            <Snowflake className="h-6 w-6 text-blue-500" />
                          </div>

                          <div className="mt-3 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">Rango válido:</span>
                              <span className="font-mono">
                                {freezer.min_temp_threshold}°C a {freezer.max_temp_threshold}°C
                              </span>
                            </div>

                            {lastLog ? (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-sm">Última lectura:</span>
                                <span
                                  className={`font-mono font-bold ${
                                    lastLog.is_out_of_range ? "text-red-600" : "text-green-600"
                                  }`}
                                >
                                  {lastLog.temperature}°C
                                </span>
                                {lastLog.is_out_of_range && (
                                  <AlertTriangle className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">
                                Sin lecturas hoy
                              </p>
                            )}

                            <div className="flex gap-2">
                              <Badge variant={hasStartLog ? "default" : "outline"}>
                                {hasStartLog ? "✓ Inicio" : "○ Inicio"}
                              </Badge>
                              <Badge variant={hasEndLog ? "default" : "outline"}>
                                {hasEndLog ? "✓ Fin" : "○ Fin"}
                              </Badge>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Historial */}
            <Card>
              <CardHeader>
                <CardTitle>Historial de Temperaturas</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="today">
                  <TabsList>
                    <TabsTrigger value="today">Hoy ({todayLogs.length})</TabsTrigger>
                    <TabsTrigger value="all">Todos ({temperatureLogs.length})</TabsTrigger>
                    <TabsTrigger value="alerts">
                      Fuera de Rango ({outOfRangeLogs.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="today" className="mt-4">
                    <TemperatureLogsTable logs={todayLogs} />
                  </TabsContent>

                  <TabsContent value="all" className="mt-4">
                    <TemperatureLogsTable logs={temperatureLogs} />
                  </TabsContent>

                  <TabsContent value="alerts" className="mt-4">
                    <TemperatureLogsTable logs={outOfRangeLogs} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function TemperatureLogsTable({ logs }: { logs: TemperatureLog[] }) {
  if (logs.length === 0) {
    return <p className="text-muted-foreground text-center py-8">Sin registros</p>;
  }

  return (
    <div className="max-h-96 overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha/Hora</TableHead>
            <TableHead>Congelador</TableHead>
            <TableHead>Temperatura</TableHead>
            <TableHead>Turno</TableHead>
            <TableHead>Registrado por</TableHead>
            <TableHead>Notas</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <TableRow
              key={log.id}
              className={log.is_out_of_range ? "bg-red-50 dark:bg-red-950" : ""}
            >
              <TableCell className="whitespace-nowrap">
                {format(new Date(log.recorded_at), "dd/MM/yyyy HH:mm", { locale: es })}
              </TableCell>
              <TableCell>
                {log.freezer?.code} - {log.freezer?.name}
              </TableCell>
              <TableCell>
                <span
                  className={`font-mono font-bold ${
                    log.is_out_of_range ? "text-red-600" : ""
                  }`}
                >
                  {log.temperature}°C
                </span>
                {log.is_out_of_range && (
                  <AlertTriangle className="h-4 w-4 text-red-500 inline ml-2" />
                )}
              </TableCell>
              <TableCell>
                <Badge variant={log.shift_type === "start" ? "default" : "secondary"}>
                  {log.shift_type === "start" ? "Inicio" : "Fin"}
                </Badge>
              </TableCell>
              <TableCell>{log.profile?.full_name || "-"}</TableCell>
              <TableCell className="max-w-48 truncate">{log.notes || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
